import React from 'react';
import { Tabs } from 'antd';
import { AreaChartOutlined, DotChartOutlined, BarChartOutlined } from '@ant-design/icons';
import RectangleAnalysis from './RectangleAnalysis';
import ComingSoon from './ComingSoon';
import PointAnalysis from './PointAnalysis';
import ErrorBoundary from '../ErrorBoundary';
import { useTranslation } from 'react-i18next';
import { useAppTheme } from '../../theme/ThemeProvider';

interface AnalysisTabsProps {
  ohlcData: any[];
  selectedRectangle?: any;
  selectedPoint?: any;
  chartApiRef?: React.RefObject<any>;
  onSaveSignal?: (analysisResult: any, selectedRectangle: any) => void;
  activeTabKey?: 'rectangle' | 'point' | 'similarPattern';
  onActiveTabKeyChange?: (key: 'rectangle' | 'point' | 'similarPattern') => void;
  pointAnalysisAutoRunToken?: number;
  rectangleAnalysisAutoRunToken?: number;
  agentRectangleAnalysisResult?: any;
}

const AnalysisTabs: React.FC<AnalysisTabsProps> = ({ 
  ohlcData, 
  selectedRectangle, 
  selectedPoint, 
  chartApiRef, 
  onSaveSignal,
  activeTabKey,
  onActiveTabKeyChange,
  pointAnalysisAutoRunToken,
  rectangleAnalysisAutoRunToken,
  agentRectangleAnalysisResult,
}) => {
  const { t } = useTranslation();
  const { theme } = useAppTheme();
  const [internalActiveKey, setInternalActiveKey] = React.useState<'rectangle' | 'point' | 'similarPattern'>('rectangle');
  const currentActiveKey = activeTabKey ?? internalActiveKey;

  return (
    <Tabs 
      activeKey={currentActiveKey}
      onChange={(key) => {
        const nextKey = key as 'rectangle' | 'point' | 'similarPattern';
        setInternalActiveKey(nextKey);
        onActiveTabKeyChange?.(nextKey);
      }}
      style={{ 
        background: theme.surfaceBackground,
        borderRadius: 8,
        padding: '16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        width: '100%',
        margin: 0
      }}
      size="large"
      items={[
        {
          key: "rectangle",
          label: (
            <span>
              <AreaChartOutlined style={{ marginRight: 8 }} />
              {t('analysis.rectangleAnalysis')}
            </span>
          ),
          children: (
            <ErrorBoundary>
              <RectangleAnalysis 
                ohlcData={ohlcData} 
                selectedRectangle={selectedRectangle} 
                chartApiRef={chartApiRef} 
                onSaveSignal={onSaveSignal}
                externalResult={agentRectangleAnalysisResult}
                autoRunToken={rectangleAnalysisAutoRunToken}
              />
            </ErrorBoundary>
          )
        },
        {
          key: "point",
          forceRender: true,
          label: (
            <span>
              <DotChartOutlined style={{ marginRight: 8 }} />
              {t('analysis.signalPointAnalysis')}
            </span>
          ),
          children: (
            <ErrorBoundary>
              <PointAnalysis
                ohlcData={ohlcData}
                selectedPoint={selectedPoint}
                onSaveSignal={onSaveSignal}
                autoRunToken={pointAnalysisAutoRunToken}
                autoRunSkipUsageCheck={true}
              />
            </ErrorBoundary>
          )
        },
        {
          key: "similarPattern",
          label: (
            <span>
              <BarChartOutlined style={{ marginRight: 8 }} />
              {t('analysis.similarPatternAnalysis')}
            </span>
          ),
          children: <ComingSoon />
        }
      ]}
    />
  );
};

export default AnalysisTabs;
